import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { LoginResponse } from '../models/login-response.model';
import { isTokenExpiredOrExpireSoon } from '../utils/token';
import { AuthBaseApi } from './auth-base-api';
import { LocalStorageKeys } from '@cms_root/app/core/models/local-storage-keys';

export abstract class AuthBase {
  private readonly _authUrl = 'waauth/auth-session/authenticate';
  protected abstract tokenName;
  protected abstract apiUrl: string;
  protected abstract authRedirectUrl: string;
  private _isAuth$ = new BehaviorSubject<boolean>(false);

  public get tokenKey(): string {
    return this.tokenName;
  }

  public get isAuth$(): Observable<boolean> {
    return this._isAuth$.asObservable();
  }

  public get isAuth(): boolean {
    return this._isAuth$.getValue();
  }

  public set isAuth(value: boolean) {
    this._isAuth$.next(value);
  }

  constructor(protected readonly authApiService: AuthBaseApi, protected readonly router: Router) {}

  public getTokenFromLocalStorage(): string {
    return localStorage.getItem(this.tokenKey);
  }

  public init(): void {
    const token = this.getTokenFromLocalStorage();

    this.isAuth = !!token && !isTokenExpiredOrExpireSoon(token);
  }

  public loginUser(authSessionId: string): Observable<LoginResponse> {
    return this.authApiService.login(authSessionId).pipe(
      tap((data) => {
        const token = data.token;
        this.setTokenInLocalStorage(token);
      })
    );
  }

  public logoutUser(): void {
    this.removeTokenFromLocalStorage();
    this.isAuth = false;
  }

  public unauthorized(redirectUrl: string): void {
    localStorage.setItem(LocalStorageKeys.authRedirect, redirectUrl);
    this.removeTokenFromLocalStorage();
    this.isAuth = false;
    this.redirectToIdp();
  }

  protected setTokenInLocalStorage(token: string): void {
    localStorage.setItem(this.tokenKey, token);
    this.isAuth = true;
  }

  protected removeTokenFromLocalStorage(): void {
    localStorage.removeItem(this.tokenKey);
  }

  protected redirectToIdp(): void {
    const redirectTo = encodeURIComponent(`${window.location.origin}/${this.authRedirectUrl}`);
    const pageHit = encodeURIComponent(window.location.href);
    const externalPage = 1;
    const redirectUrl = `${
      this.apiUrl + this._authUrl
    }?hitPageData[redirectTo]=${redirectTo}&hitPageData[pageHit]=${pageHit}&hitPageData[externalPage]=${externalPage}`;

    window.location.href = redirectUrl;
  }
}
